import React, { useContext, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { BusinessAutomationActionTypeEnum } from '@reach/shared-types/business-automation';
import type { AutomationTemplateContents } from '@reach/shared-types/automation-template';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { StepDialogFooter } from '../StepDialogFooter';
import { SendMessageForm } from './SendMessageForm';
import { StripoDialog } from './stripo';
import styles from './SendMessageStep.module.css';
import { usePartnerProfile } from '@/hooks/usePartnerSettings';
import type { SendMessageFormField } from './types';

type SendMessageAction = Extract<
  AutomationTemplateContents['actionData'][0],
  { type: BusinessAutomationActionTypeEnum.SEND_COMMUNICATION }
>;

interface SendMessageDialogProps {
  open: boolean;
  onClose: () => void;
  action: SendMessageAction;
  actionIndex: number;
}

export const SendMessageDialog: React.FC<SendMessageDialogProps> = ({
  open,
  onClose,
  action,
  actionIndex,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateAction, updateUsePointOfContactSender } = editor;
  const { partner } = usePartnerProfile();

  const [formData, setFormData] = useState({
    subject: action.subject || '',
    previewText: action.previewText || '',
    html: action.html || '',
    css: action.css || '',
    compiledHtml: action.compiledHtml || '',
    text: action.text || '',
    sendToPOC: !!editor.state.localContents.usePointOfContactSender,
  });

  const effectiveHtml = formData.html || partner?.emailTemplateOverride?.html || '';
  const effectiveCss = formData.css || partner?.emailTemplateOverride?.css || '';
  const [stripoIsOpen, setShowStripo] = useState(false);

  // Reset form data when dialog opens or action changes
  useEffect(() => {
    if (open) {
      setFormData({
        subject: action.subject || '',
        previewText: action.previewText || '',
        html: action.html || '',
        css: action.css || '',
        compiledHtml: action.compiledHtml || '',
        text: action.text || '',
        sendToPOC: !!editor.state.localContents.usePointOfContactSender,
      });
    }
  }, [open, action, editor.state.localContents.usePointOfContactSender]);

  if (!open) return null;

  const handleOK = () => {
    const { sendToPOC, ...actionData } = formData;

    updateAction(actionIndex, {
      subject: actionData.subject,
      previewText: actionData.previewText,
      html: actionData.html,
      css: actionData.css,
      compiledHtml: actionData.compiledHtml,
      text: actionData.text,
    });
    updateUsePointOfContactSender(sendToPOC);
    onClose();
  };

  const setFormFieldValue = (field: SendMessageFormField, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const showStripo = () => setShowStripo(true);
  const hideStripo = () => setShowStripo(false);

  const handleStripoSave = (newHtml: string, newCss: string, compiledHtml: string) => {
    setFormData(prev => ({
      ...prev,
      html: newHtml,
      css: newCss,
      compiledHtml,
    }));
    setShowStripo(false);
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header>
          <h2>Configure Send Message</h2>
          <p>Compose your message content</p>
          <button onClick={onClose}>
            <X />
          </button>
        </header>

        <main>
          <SendMessageForm
            subject={formData.subject}
            previewText={formData.previewText}
            html={effectiveHtml}
            css={effectiveCss}
            text={formData.text}
            onChange={setFormFieldValue}
            onOpenStripoEditor={showStripo}
            sendToPointOfContact={formData.sendToPOC}
          />
        </main>

        <StepDialogFooter onCancel={onClose} onOK={handleOK} />
      </dialog>

      <StripoDialog
        open={stripoIsOpen}
        onClose={hideStripo}
        html={effectiveHtml}
        css={effectiveCss}
        onSave={handleStripoSave}
      />
    </>
  );
};
